import { useEffect, useState, useCallback } from "react";
import { Plus, Upload, Loader2, Pencil } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loading, StatusBadge, EmptyState, OfficeSelect, groupByOffice, OfficeSectionHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const ROLES = [{ v: "staff", l: "従業者" }, { v: "office_admin", l: "事業所管理者" }, { v: "corp_admin", l: "法人管理者" }];
const STATUSES = ["在籍", "休職", "退職", "利用停止"];
const empty = { login_id: "", name: "", role: "staff", office_ids: [], status: "在籍", password: "", hire_date: "", phone: "", line_user_id: "" };

export default function Staff() {
  const [users, setUsers] = useState(null);
  const [offices, setOffices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvRows, setCsvRows] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [officeId, setOfficeId] = useState("");

  const load = useCallback(() => { api.get("/users").then(({ data }) => setUsers(data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "" }); setOpen(true); };

  const save = async () => {
    if (!form.login_id || !form.name) { toast.error("ログインIDと氏名は必須です"); return; }
    try {
      if (editing) { await api.put(`/users/${editing.id}`, form); toast.success("更新しました"); }
      else { await api.post("/users", form); toast.success("職員を登録しました"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvLoading(true); setCsvRows(null);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/users/csv/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }); setCsvRows(data); }
    catch (err) { toast.error(apiErr(err)); } finally { setCsvLoading(false); }
  };

  const commitCsv = async () => {
    try { const { data } = await api.post("/users/csv/commit", { rows: csvRows.rows }); toast.success(`${data.created}名を登録しました`); setCsvOpen(false); setCsvRows(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!users) return <Loading />;

  const staffHeader = (
    <TableRow><TableHead>ログインID</TableHead><TableHead>氏名</TableHead><TableHead>権限</TableHead><TableHead>所属事業所</TableHead><TableHead>在籍状態</TableHead><TableHead></TableHead></TableRow>
  );
  const renderStaffRows = (list) => list.map((u) => (
    <TableRow key={u.id} data-testid={`staff-row-${u.id}`}>
      <TableCell className="font-mono text-sm">{u.login_id}</TableCell>
      <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
      <TableCell><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{u.role_label}</span></TableCell>
      <TableCell className="text-sm text-slate-500">{u.office_names?.join(" / ") || "-"}</TableCell>
      <TableCell><StatusBadge status={u.status} /></TableCell>
      <TableCell><button onClick={() => openEdit(u)} className="text-slate-400 hover:text-blue-600" data-testid={`edit-staff-${u.id}`}><Pencil className="w-4 h-4" /></button></TableCell>
    </TableRow>
  ));
  const filtered = officeId ? users.filter((u) => (u.office_ids || []).includes(officeId)) : users;
  const sections = officeId ? [] : groupByOffice(users, offices, (u) => u.office_ids);

  return (
    <div>
      <PageHeader
        title="職員管理" subtitle={`登録職員 ${users.length}名`}
        actions={
          <div className="flex gap-2 items-center">
            <OfficeSelect offices={offices} value={officeId} onChange={setOfficeId} testid="staff-office-filter" />
            <Dialog open={csvOpen} onOpenChange={(o) => { setCsvOpen(o); if (!o) setCsvRows(null); }}>
              <DialogTrigger asChild><Button variant="outline" data-testid="csv-import-btn"><Upload className="w-4 h-4 mr-1" /> CSV一括登録</Button></DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>CSV一括登録</DialogTitle>
                  <DialogDescription>列：login_id, name, password, office, concurrent_office</DialogDescription>
                </DialogHeader>
                <input type="file" accept=".csv" id="csvfile" className="hidden" onChange={onCsv} />
                <Button variant="outline" size="sm" className="w-fit" onClick={() => document.getElementById("csvfile").click()} data-testid="csv-file-btn">
                  {csvLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} CSVファイルを選択
                </Button>
                {csvRows && (
                  <div>
                    <div className="text-sm text-slate-600 my-2">全{csvRows.total}行中 {csvRows.valid_count}行が登録可能（登録前に確認してください）</div>
                    <div className="max-h-72 overflow-y-auto border rounded-lg">
                      <Table>
                        <TableHeader><TableRow><TableHead>行</TableHead><TableHead>ログインID</TableHead><TableHead>氏名</TableHead><TableHead>結果</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {csvRows.rows.map((r) => (
                            <TableRow key={r.row} className={r.valid ? "" : "bg-rose-50"}>
                              <TableCell>{r.row}</TableCell><TableCell>{r.login_id}</TableCell><TableCell>{r.name}</TableCell>
                              <TableCell className="text-xs">{r.valid ? <span className="text-green-600">OK</span> : <span className="text-rose-600">{r.errors.join("、")}</span>}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <DialogFooter className="mt-3"><Button onClick={commitCsv} disabled={!csvRows.valid_count} className="bg-blue-600 hover:bg-blue-700" data-testid="csv-commit-btn">{csvRows.valid_count}名を登録</Button></DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-1" /> 職員を追加</Button>
          </div>
        }
      />

      {(officeId ? filtered.length : users.length) ? (
        officeId ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table><TableHeader>{staffHeader}</TableHeader><TableBody>{renderStaffRows(filtered)}</TableBody></Table>
          </div>
        ) : (
          <div className="space-y-4">
            {sections.map((sec) => (
              <div key={sec.office.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <OfficeSectionHeader name={sec.office.name} count={sec.items.length} />
                <Table><TableHeader>{staffHeader}</TableHeader><TableBody>{renderStaffRows(sec.items)}</TableBody></Table>
              </div>
            ))}
          </div>
        )
      ) : <EmptyState title="職員がいません" />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "職員を編集" : "職員を追加"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>ログインID *</Label><Input value={form.login_id} disabled={!!editing} onChange={(e) => setForm({ ...form, login_id: e.target.value })} className="mt-1" data-testid="staff-loginid-input" /></div>
              <div><Label>氏名 *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="staff-name-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>権限</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>在籍状態</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>所属事業所（兼務可）</Label>
              <div className="mt-1 space-y-1 max-h-32 overflow-y-auto border rounded-lg p-2">
                {offices.map((o) => (
                  <label key={o.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                    <Checkbox checked={form.office_ids.includes(o.id)} onCheckedChange={(c) => setForm({ ...form, office_ids: c ? [...form.office_ids, o.id] : form.office_ids.filter((x) => x !== o.id) })} data-testid={`staff-office-${o.id}`} />
                    {o.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>電話番号（WhatsApp用）</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+819012345678" className="mt-1" data-testid="staff-phone-input" /></div>
              <div><Label>LINEユーザーID</Label><Input value={form.line_user_id || ""} onChange={(e) => setForm({ ...form, line_user_id: e.target.value })} className="mt-1" data-testid="staff-line-input" /></div>
            </div>
            <div><Label>{editing ? "パスワード（変更する場合のみ）" : "初期パスワード（未入力で既定値）"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="staff-password-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-staff-btn">保存</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
